// frontend/components/LiveChat.js - COMPLETE FIXED VERSION
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Send, X, MessageCircle, AlertCircle, Loader, Trash2, User, Clock, Mail, Wifi, WifiOff } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';

// 🔧 CUSTOM HOOK: Polling Manager
const usePollingManager = () => {
  const intervalsRef = useRef(new Map());
  const timeoutsRef = useRef(new Map());
  
  const setPollingInterval = useCallback((key, callback, interval) => {
    clearPollingInterval(key);
    const id = setInterval(callback, interval);
    intervalsRef.current.set(key, id);
    return id;
  }, []);
  
  const clearPollingInterval = useCallback((key) => {
    if (intervalsRef.current.has(key)) {
      clearInterval(intervalsRef.current.get(key));
      intervalsRef.current.delete(key);
    }
  }, []);
  
  const setPollingTimeout = useCallback((key, callback, delay) => {
    clearPollingTimeout(key);
    const id = setTimeout(callback, delay);
    timeoutsRef.current.set(key, id);
    return id;
  }, []);
  
  const clearPollingTimeout = useCallback((key) => {
    if (timeoutsRef.current.has(key)) {
      clearTimeout(timeoutsRef.current.get(key));
      timeoutsRef.current.delete(key);
    }
  }, []);
  
  const clearAllPolling = useCallback(() => {
    intervalsRef.current.forEach((id) => clearInterval(id));
    timeoutsRef.current.forEach((id) => clearTimeout(id));
    intervalsRef.current.clear();
    timeoutsRef.current.clear();
  }, []);
  
  return {
    setPollingInterval,
    clearPollingInterval,
    setPollingTimeout,
    clearPollingTimeout,
    clearAllPolling
  };
};

export default function LiveChat() {
  const { user, isAuthenticated } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [step, setStep] = useState('initial');
  const [guestInfo, setGuestInfo] = useState({ name: '', email: '' });
  const [threadId, setThreadId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [error, setError] = useState('');
  const [threads, setThreads] = useState([]);
  const [selectedThread, setSelectedThread] = useState(null);
  const [showEndConfirm, setShowEndConfirm] = useState(false);
  const [adminOnline, setAdminOnline] = useState(false);
  const [waitingTime, setWaitingTime] = useState('less than 1 hour');
  const [emailNotificationSent, setEmailNotificationSent] = useState(false);
  const [guestToken, setGuestToken] = useState(null);
  
  const messagesEndRef = useRef(null);
  const lastMessageIdRef = useRef(null);
  const isMounted = useRef(true);
  const lastFetchTimeRef = useRef({});
  
  // 🔧 Use custom polling manager
  const {
    setPollingInterval,
    clearPollingInterval,
    clearAllPolling
  } = usePollingManager();

  // 🔒 Load guest token from localStorage
  useEffect(() => {
    if (!isAuthenticated) {
      const storedToken = localStorage.getItem('guestChatToken');
      const expiry = localStorage.getItem('guestTokenExpiry');
      
      if (storedToken && expiry && Date.now() < parseInt(expiry)) {
        setGuestToken(storedToken);
      } else if (expiry && Date.now() >= parseInt(expiry)) {
        // Clean expired tokens
        localStorage.removeItem('guestChatToken');
        localStorage.removeItem('guestTokenExpiry');
      }
    }
  }, [isAuthenticated]);

  // Add this to your state variables
  const [hasShownOnlineMessage, setHasShownOnlineMessage] = useState(false);

  // Update checkAdminStatus function
  const checkAdminStatus = useCallback(async () => {
    try {
      const result = await api.checkAdminOnline();
      
      if (result.success) {
        const wasOffline = !adminOnline && result.data.isOnline;
        setAdminOnline(result.data.isOnline);
        setWaitingTime(result.data.waitingTime || 'less than 1 hour');
        
        // Only show message when admin JUST came online, we're in chat, email was sent, and we haven't shown it yet
        if (wasOffline && emailNotificationSent && step === 'chat' && threadId && !hasShownOnlineMessage) {
          setMessages(prev => [...prev, {
            message_id: `online_${Date.now()}`,
            sender_type: 'system',
            sender_name: 'System',
            content: 'Great news! Our team is now online and will respond shortly.',
            created_at: new Date().toISOString()
          }]);
          setHasShownOnlineMessage(true);
        }
        
        // Reset the flag when admin goes offline
        if (!result.data.isOnline) {
          setHasShownOnlineMessage(false);
        }
      } else {
        setAdminOnline(false);
        setWaitingTime('less than 1 hour');
        setHasShownOnlineMessage(false);
      }
    } catch (err) {
      console.error('Error checking admin status:', err);
      setAdminOnline(false);
      setWaitingTime('less than 1 hour');
      setHasShownOnlineMessage(false);
    }
  }, [adminOnline, emailNotificationSent, step, threadId, hasShownOnlineMessage]);

  // Also reset the flag when chat session is cleared
  const clearChatSession = useCallback(() => {
    setThreadId(null);
    setMessages([]);
    setSelectedThread(null);
    setStep(isAuthenticated ? 'initial' : 'guest-form');
    setGuestInfo({ name: '', email: '' });
    setEmailNotificationSent(false);
    setHasShownOnlineMessage(false); // Reset this flag too
    lastMessageIdRef.current = null;
    clearAllPolling();
    
    if (!isAuthenticated) {
      localStorage.removeItem('guestChatThreadId');
      localStorage.removeItem('guestChatMessages');
      localStorage.removeItem('guestChatInfo');
    }
  }, [isAuthenticated, clearAllPolling]);

  useEffect(() => {
    if (isAuthenticated && isOpen && step === 'initial') {
      loadUserThreads();
    }
  }, [isAuthenticated, isOpen, step]);

  // Admin status polling
  useEffect(() => {
    if (isOpen) {
      checkAdminStatus();
      const interval = setInterval(checkAdminStatus, 15000); 
      return () => clearInterval(interval);
    }
  }, [isOpen, checkAdminStatus]);

  // 🔧 Message polling with guest token support
  useEffect(() => {
    if (threadId && step === 'chat' && isMounted.current) {
      setPollingInterval('message-polling', pollForMessages, 3000);
    } else {
      clearPollingInterval('message-polling');
    }
    
    return () => {
      clearPollingInterval('message-polling');
    };
  }, [threadId, step, setPollingInterval, clearPollingInterval]);

  // 🔧 Optimized message polling function
  const pollForMessages = useCallback(async () => {
    if (!threadId || step !== 'chat' || !isMounted.current) return;
    
    try {
      const now = Date.now();
      const lastFetch = lastFetchTimeRef.current[threadId] || 0;
      
      if (now - lastFetch < 3000) return;
      
      lastFetchTimeRef.current[threadId] = now;
      
      const lastMsgId = lastMessageIdRef.current || 
                       messages[messages.length - 1]?.message_id;
      
      // 🔒 Include guest token if available
      const params = {};
      if (guestToken && !isAuthenticated) {
        params.guestToken = guestToken;
      }
      
      const result = await api.getChatNewMessages(threadId, lastMsgId, params);
      
      if (result.success) {
        const newMessages = result.data.messages || [];
        
        if (newMessages.length > 0) {
          const existingIds = new Set(messages.map(m => m.message_id));
          const uniqueNewMessages = newMessages.filter(msg => !existingIds.has(msg.message_id));
          
          if (uniqueNewMessages.length > 0) {
            setMessages(prev => {
              const allMessages = [...prev, ...uniqueNewMessages];
              const sorted = allMessages.sort((a, b) => 
                new Date(a.created_at) - new Date(b.created_at)
              );
              const deduped = Array.from(
                new Map(sorted.map(msg => [msg.message_id, msg])).values()
              );
              return deduped;
            });
            
            lastMessageIdRef.current = uniqueNewMessages[uniqueNewMessages.length - 1].message_id;
            
            setTimeout(() => {
              if (messagesEndRef.current && isMounted.current) {
                messagesEndRef.current.scrollIntoView({ 
                  behavior: 'smooth',
                  block: 'end' 
                });
              }
            }, 100);
          }
        }
      }
    } catch (err) {
      console.error('Polling error:', err);
    }
  }, [threadId, step, messages, guestToken, isAuthenticated]);

  const loadUserThreads = async () => {
    try {
      setIsLoading(true);
      const result = await api.getChatThreads();
      if (result.success) {
        setThreads(result.data.threads || []);
      }
    } catch (err) {
      console.error('Error loading threads:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const loadThreadMessages = async (thread) => {
    try {
      setIsLoading(true);
      setError('');
      
      clearPollingInterval('message-polling');
      lastMessageIdRef.current = null;
      lastFetchTimeRef.current[thread.thread_id] = Date.now();

      // 🔒 Include guest token for guest threads
      const params = {};
      if (guestToken && !isAuthenticated) {
        params.guestToken = guestToken;
      }
      
      const result = await api.getChatThread(thread.thread_id, params);
      if (result.success) {
        const threadData = result.data.thread;
        const threadMessages = threadData.messages || [];
        
        if (threadMessages.length > 0) {
          lastMessageIdRef.current = threadMessages[threadMessages.length - 1].message_id;
        }
        
        const updatedThread = {
          ...threadData,
          participantName: threadData.guest_name || threadData.participantName || 'User',
          participantEmail: threadData.guest_email || threadData.participantEmail || '',
          subject: threadData.subject || 'No subject',
          status: threadData.status || 'active',
        };
        
        setSelectedThread(updatedThread);
        setMessages(threadMessages);
        setThreadId(threadData.thread_id);
        
        if (!isAuthenticated) {
          const guestData = {
            name: threadData.guest_name || '',
            email: threadData.guest_email || ''
          };
          setGuestInfo(guestData);
          localStorage.setItem('guestChatInfo', JSON.stringify(guestData));
          localStorage.setItem('guestChatThreadId', threadData.thread_id);
        }
        
        setStep('chat');
      } else {
        setError('Failed to load chat');
      }
    } catch (err) {
      setError('Failed to load chat');
    } finally {
      setIsLoading(false);
    }
  };

  const handleStartGuestChat = async (e) => {
    e.preventDefault();
    
    if (!guestInfo.name.trim() || !guestInfo.email.trim()) {
      setError('Please enter your name and email');
      return;
    }

    if (!/^\S+@\S+\.\S+$/.test(guestInfo.email)) {
      setError('Please enter a valid email address');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      // 🔒 Check for existing threads using guest token if available
      const params = {};
      if (guestToken) {
        params.guestToken = guestToken;
      } else {
        params.email = guestInfo.email;
        params.name = guestInfo.name;
      }
      
      const existingResult = await api.getGuestThreadsByEmail(params);

      let threadToUse = null;

      if (existingResult.success && existingResult.data.threads.length > 0) {
        const activeThreads = existingResult.data.threads.filter(
          t => t.status !== 'ended_by_user' && t.status !== 'closed'
        );
        
        if (activeThreads.length > 0) {
          threadToUse = activeThreads[0];
        }
      }

      if (threadToUse) {
        await loadThreadMessages(threadToUse);
      } else {
        const result = await api.createChatThread({
          name: guestInfo.name.trim(),
          email: guestInfo.email.trim().toLowerCase(),
          message: "Hello, I need assistance",
          isGuest: true
        });

        if (result.success) {
          const thread = result.data.thread;
          setThreadId(thread.thread_id);
          setMessages(thread.messages || []);
          setStep('chat');
          
          if (thread.messages?.length > 0) {
            lastMessageIdRef.current = thread.messages[thread.messages.length - 1].message_id;
          }
          
          // 🔒 Save guest token for future requests
          if (result.data.guestToken) {
            setGuestToken(result.data.guestToken);
            localStorage.setItem('guestChatToken', result.data.guestToken);
            localStorage.setItem('guestTokenExpiry', Date.now() + 7 * 24 * 60 * 60 * 1000);
          }
          
          localStorage.setItem('guestChatThreadId', thread.thread_id);
          localStorage.setItem('guestChatInfo', JSON.stringify({
            name: guestInfo.name,
            email: guestInfo.email
          }));
          
          setTimeout(() => {
            setMessages(prev => [...prev, {
              message_id: `welcome_${Date.now()}`,
              sender_type: 'system',
              sender_name: 'System',
              content: `Welcome! Our support team is currently ${adminOnline ? 'online' : 'offline'}. ${!adminOnline ? 'If no one is live, you will receive email notifications for responses.' : ''}`,
              created_at: new Date().toISOString()
            }]);
          }, 500);
        } else {
          setError(result.error || 'Failed to start chat');
        }
      }
    } catch (err) {
      setError('Failed to start chat');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!inputMessage.trim() || isLoading) return;

    try {
      setIsLoading(true);
      setError('');
      
      if (!threadId) {
        const threadData = {
          name: isAuthenticated ? user.username : guestInfo.name,
          email: isAuthenticated ? user.email : guestInfo.email,
          message: inputMessage,
          isGuest: !isAuthenticated
        };

        const result = await api.createChatThread(threadData);
        if (result.success) {
          const thread = result.data.thread;
          setThreadId(thread.thread_id);
          setMessages(thread.messages || []);
          
          // FIX: Use thread.messages instead of threadMessages
          if (thread.messages && thread.messages.length > 0) {
            lastMessageIdRef.current = thread.messages[thread.messages.length - 1].message_id;
          }
          
          if (!isAuthenticated && result.data.guestToken) {
            setGuestToken(result.data.guestToken);
            localStorage.setItem('guestChatToken', result.data.guestToken);
            localStorage.setItem('guestTokenExpiry', Date.now() + 7 * 24 * 60 * 60 * 1000);
          }
          
          if (!isAuthenticated) {
            localStorage.setItem('guestChatThreadId', thread.thread_id);
          }
        } else {
          throw new Error(result.error || 'Failed to create chat');
        }
      } else {
        // Include guest token in request if available
        const requestData = {
          content: inputMessage
        };
        
        const headers = {};
        if (guestToken && !isAuthenticated) {
          headers['X-Guest-Token'] = guestToken;
        }
        
        const result = await api.sendChatMessage(threadId, requestData, { headers });

        if (result.success && !result.data.duplicate) {
          if (result.data.message) {
            setMessages(prev => {
              const newMessages = [...prev, result.data.message];
              lastMessageIdRef.current = result.data.message.message_id;
              return newMessages;
            });
          }
          
          if (result.data.emailNotification) {
            setEmailNotificationSent(true);
            setTimeout(() => {
              setMessages(prev => [...prev, {
                message_id: `notification_${Date.now()}`,
                sender_type: 'system',
                sender_name: 'System',
                content: `Our team is currently offline. We've sent them an email notification with your message. They'll respond within ${waitingTime}.`,
                created_at: new Date().toISOString()
              }]);
            }, 1000);
          }
        }
      }

      setInputMessage('');
    } catch (err) {
      setError(err.message || 'Failed to send message');
    } finally {
      setIsLoading(false);
    }
  };

  const handleEndChat = async () => {
    if (!threadId) return;

    try {
      setIsLoading(true);
      clearPollingInterval('message-polling');
      
      const data = {};
      const headers = {};
      
      if (!isAuthenticated) {
        data.email = guestInfo.email;
        data.name = guestInfo.name;
        if (guestToken) {
          headers['X-Guest-Token'] = guestToken;
        }
      }
      
      const result = await api.endChatThread(threadId, data, { headers });
      
      if (result.success) {
        setMessages(prev => [...prev, {
          message_id: `system_${Date.now()}`,
          sender_type: 'system',
          sender_name: 'System',
          content: 'Chat ended successfully. Thank you for chatting with us!',
          created_at: new Date().toISOString()
        }]);
        
        setTimeout(() => {
          clearChatSession();
          setShowEndConfirm(false);
        }, 2000);
      } else {
        throw new Error(result.error);
      }
    } catch (err) {
      setError(err.message || 'Failed to end chat');
    } finally {
      setIsLoading(false);
    }
  };

  const handleNewChat = () => {
    clearChatSession();
    setError('');
  };

  const handleClose = () => {
    clearAllPolling();
    setIsOpen(false);
  };

  const formatTime = (timestamp) => {
    try {
      const date = new Date(timestamp);
      return date.toLocaleTimeString('en-US', { 
        hour: '2-digit', 
        minute: '2-digit',
        hour12: true 
      });
    } catch (e) {
      return '';
    }
  };

  const formatDate = (timestamp) => {
    try {
      const date = new Date(timestamp);
      const today = new Date();
      
      if (date.toDateString() === today.toDateString()) {
        return 'Today';
      }
      
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);
      if (date.toDateString() === yesterday.toDateString()) {
        return 'Yesterday';
      }
      
      return date.toLocaleDateString('en-US', { 
        month: 'short', 
        day: 'numeric' 
      });
    } catch (e) {
      return '';
    }
  };

  const getMessageAlignment = (message) => {
    if (!message || !message.sender_type) return { align: 'left', isCurrentUser: false };
    
    const isCurrentUser = 
      (isAuthenticated && message.sender_type === 'user' && message.sender_id === user?.id) ||
      (!isAuthenticated && message.sender_type === 'guest');
    
    return {
      align: isCurrentUser ? 'right' : 'left',
      isCurrentUser,
      isAdmin: message.sender_type === 'admin',
      isSupport: message.sender_type === 'support_agent'
    };
  };

  const getSenderName = (message) => {
    if (!message) return '';
    
    const alignment = getMessageAlignment(message);
    
    if (alignment.isCurrentUser) {
      return isAuthenticated ? 'You' : guestInfo.name || 'You';
    }
    
    if (alignment.isAdmin) {
      return `${message.sender_name || 'Admin'} 👑`;
    }
    
    if (alignment.isSupport) {
      return `${message.sender_name || 'Support'} 🛠️`;
    }
    
    return message.sender_name || 'User';
  };

  // 🔧 Component cleanup
  useEffect(() => {
    isMounted.current = true;
    
    return () => {
      isMounted.current = false;
      clearAllPolling();
    };
  }, [clearAllPolling]);

  return (
    <>
      {isOpen && (
        <div className="fixed inset-0 md:inset-auto md:bottom-24 md:right-6 md:top-auto md:max-h-[600px] z-50 w-full md:w-96 bg-white dark:bg-gray-800 md:rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-700 flex flex-col overflow-hidden animate-slideUp">
          <div className="bg-gradient-to-r from-blue-600 to-indigo-600 p-4 flex items-center justify-between text-white sticky top-0 z-10">
            <div className="flex items-center gap-3 flex-1">
              <div className="relative">
                <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
                  <MessageCircle className="w-5 h-5" />
                </div>
                <span className={`absolute -top-1 -right-1 w-2 h-2 rounded-full animate-pulse ${adminOnline ? 'bg-green-400' : 'bg-yellow-400'}`}></span>
              </div>
              <div>
                <h3 className="font-bold text-sm">Live Support</h3>
                <p className="text-xs text-blue-100">
                  {adminOnline ? "We're online! 🟢" : "We'll respond via email 📧"}
                </p>
              </div>
            </div>
            <button
              onClick={handleClose}
              className="text-white hover:bg-white/20 p-1 rounded-full transition"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto bg-gray-50 dark:bg-gray-900">
            {step === 'initial' && (
              <div className="p-4 space-y-4">
                <div className="text-center">
                  <div className="w-16 h-16 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center mx-auto mb-3">
                    <MessageCircle className="w-8 h-8 text-blue-600 dark:text-blue-400" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">
                    Chat Support
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                    How can we help you today?
                  </p>
                  
                  <div className="mb-4">
                    <div className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium mb-1 ${
                      adminOnline 
                        ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                        : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400'
                    }`}>
                      <span className={`w-2 h-2 rounded-full mr-2 ${adminOnline ? 'bg-green-500' : 'bg-yellow-500'} animate-pulse`}></span>
                      {adminOnline ? 'Team is online 🟢' : 'Team is offline ⏸️'}
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      Expected response time: <span className="font-semibold">{waitingTime}</span>
                    </p>
                    {!adminOnline && (
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        If no one is live, you'll receive email notifications for responses
                      </p>
                    )}
                  </div>
                </div>

                {isAuthenticated && threads.length > 0 && (
                  <div className="mb-6">
                    <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
                      Your Previous Chats
                    </h4>
                    <div className="space-y-2 max-h-60 overflow-y-auto">
                      {threads.map((thread) => (
                        <button
                          key={thread.thread_id}
                          onClick={() => loadThreadMessages(thread)}
                          className="w-full text-left p-3 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg hover:border-blue-500 dark:hover:border-blue-400 transition-all duration-200"
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                                {thread.subject || 'Support Chat'}
                              </p>
                              <div className="flex items-center gap-2 mt-1">
                                <Clock className="w-3 h-3 text-gray-400" />
                                <span className="text-xs text-gray-500 dark:text-gray-400">
                                  {formatDate(thread.updated_at)}
                                </span>
                                <span className={`text-xs px-2 py-0.5 rounded-full ${
                                  thread.status === 'active' 
                                    ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                                    : thread.status === 'resolved'
                                    ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
                                    : 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300'
                                }`}>
                                  {thread.status}
                                </span>
                              </div>
                            </div>
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                <button
                  onClick={() => setStep(isAuthenticated ? 'chat' : 'guest-form')}
                  className="w-full px-4 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-lg font-semibold hover:shadow-lg transition-all active:scale-95 flex items-center justify-center gap-2"
                >
                  <MessageCircle className="w-4 h-4" />
                  Start New Chat
                </button>
              </div>
            )}

            {step === 'guest-form' && (
              <form onSubmit={handleStartGuestChat} className="p-4 space-y-4">
                <div className="text-center mb-4">
                  <User className="w-12 h-12 text-blue-500 mx-auto mb-2" />
                  <h3 className="text-lg font-bold text-gray-900 dark:text-white">
                    Welcome Guest
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Please provide your details to continue
                  </p>
                  
                  <div className="mt-3 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                        Team Status:
                      </span>
                      <span className={`text-xs px-2 py-1 rounded-full ${adminOnline ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>
                        {adminOnline ? 'Online' : 'Offline'}
                      </span>
                    </div>
                    <p className="text-xs text-gray-600 dark:text-gray-400">
                      Response time: {waitingTime}
                    </p>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Your Name
                  </label>
                  <input
                    type="text"
                    value={guestInfo.name}
                    onChange={(e) => setGuestInfo({ ...guestInfo, name: e.target.value.trim() })}
                    placeholder="John Doe"
                    className="w-full px-4 py-3 border-2 border-gray-300 dark:border-gray-600 rounded-lg focus:border-blue-500 dark:bg-gray-700 dark:text-white outline-none transition"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Your Email
                  </label>
                  <input
                    type="email"
                    value={guestInfo.email}
                    onChange={(e) => setGuestInfo({ ...guestInfo, email: e.target.value.trim().toLowerCase() })}
                    placeholder="john@example.com"
                    className="w-full px-4 py-3 border-2 border-gray-300 dark:border-gray-600 rounded-lg focus:border-blue-500 dark:bg-gray-700 dark:text-white outline-none transition"
                    required
                  />
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    We'll send email notifications if the team is offline
                  </p>
                </div>

                {error && (
                  <div className="flex items-start gap-2 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                    <AlertCircle className="w-4 h-4 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
                  </div>
                )}

                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setStep('initial')}
                    className="flex-1 px-4 py-3 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg font-semibold hover:bg-gray-300 dark:hover:bg-gray-600 transition"
                  >
                    Back
                  </button>
                  <button
                    type="submit"
                    disabled={isLoading || !guestInfo.name || !guestInfo.email}
                    className="flex-1 px-4 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-lg font-semibold hover:shadow-lg transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {isLoading ? (
                      <Loader className="w-4 h-4 animate-spin" />
                    ) : (
                      <>
                        <MessageCircle className="w-4 h-4" />
                        Start Chat
                      </>
                    )}
                  </button>
                </div>
              </form>
            )}

            {step === 'chat' && (
              <div className="flex flex-col h-full">
                <div className="p-4 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full flex items-center justify-center">
                        <span className="text-white font-bold text-sm">
                          {isAuthenticated ? user.username?.charAt(0)?.toUpperCase() : guestInfo.name?.charAt(0)?.toUpperCase() || 'G'}
                        </span>
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900 dark:text-white">
                          {isAuthenticated ? user.username : guestInfo.name}
                        </h4>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          {isAuthenticated ? user.email : guestInfo.email}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className={`px-2 py-1 rounded-full text-xs font-semibold flex items-center gap-1 ${
                        adminOnline 
                          ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                          : 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400'
                      }`}>
                        {adminOnline ? (
                          <>
                            <Wifi className="w-3 h-3" />
                            Online
                          </>
                        ) : (
                          <>
                            <WifiOff className="w-3 h-3" />
                            Offline
                          </>
                        )}
                      </div>
                      
                      <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                        selectedThread?.status === 'active'
                          ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                          : 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
                      }`}>
                        {selectedThread?.status || 'active'}
                      </span>
                    </div>
                  </div>
                  
                  <div className="mt-2">
                    <div className="flex items-center justify-between text-xs">
                      <div className="flex items-center gap-1 text-gray-600 dark:text-gray-400">
                        <Clock className="w-3 h-3" />
                        <span>Response time: <span className="font-semibold">{waitingTime}</span></span>
                      </div>
                      {emailNotificationSent && (
                        <div className="flex items-center gap-1 text-blue-600 dark:text-blue-400">
                          <Mail className="w-3 h-3" />
                          <span className="text-xs">Email sent to team</span>
                        </div>
                      )}
                    </div>
                    {!adminOnline && (
                      <div className="mt-1 text-xs text-yellow-600 dark:text-yellow-400">
                        ⚡ If no one is live, you'll get email responses
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-gradient-to-b from-white to-gray-50 dark:from-gray-800 dark:to-gray-900">
                  {messages.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-full text-gray-500 dark:text-gray-400 py-12">
                      <MessageCircle className="w-16 h-16 mb-4 opacity-30" />
                      <p className="text-sm font-medium">No messages yet</p>
                      <p className="text-xs mt-1">Start the conversation!</p>
                      <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg max-w-xs">
                        <p className="text-xs text-blue-700 dark:text-blue-300">
                          💡 Our team is {adminOnline ? 'online' : 'offline'}. 
                          {!adminOnline && " If no one responds live, we'll email you."}
                        </p>
                      </div>
                    </div>
                  ) : (
                    <>
                      {messages.map((message, index) => {
                        const alignment = getMessageAlignment(message);
                        const senderName = getSenderName(message);
                        const showDate = index === 0 || 
                          new Date(message.created_at).toDateString() !== 
                          new Date(messages[index - 1].created_at).toDateString();
                        const uniqueKey = `${message.message_id}_${message.created_at}_${index}`;

                        return (
                          <div key={uniqueKey}>
                            {showDate && (
                              <div className="flex justify-center my-4">
                                <span className="px-3 py-1 bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-400 text-xs rounded-full">
                                  {formatDate(message.created_at)}
                                </span>
                              </div>
                            )}

                            <div className={`flex ${alignment.align === 'right' ? 'justify-end' : 'justify-start'} mb-2`}>
                              <div className={`max-w-[80%] md:max-w-xs ${
                                alignment.align === 'right' 
                                  ? 'order-2 ml-2' 
                                  : 'order-1 mr-2'
                              }`}>
                                <div className={`mb-1 ${alignment.align === 'right' ? 'text-right' : 'text-left'}`}>
                                  <span className={`text-xs font-semibold ${
                                    alignment.isAdmin ? 'text-red-600 dark:text-red-400' :
                                    alignment.isSupport ? 'text-blue-600 dark:text-blue-400' :
                                    'text-gray-600 dark:text-gray-400'
                                  }`}>
                                    {senderName}
                                  </span>
                                </div>

                                <div className={`relative rounded-2xl px-4 py-3 shadow-sm ${
                                  alignment.align === 'right'
                                    ? 'bg-gradient-to-r from-blue-500 to-indigo-600 text-white rounded-br-none'
                                    : alignment.isAdmin
                                    ? 'bg-gradient-to-r from-red-500 to-pink-600 text-white rounded-bl-none'
                                    : alignment.isSupport
                                    ? 'bg-gradient-to-r from-blue-400 to-cyan-500 text-white rounded-bl-none'
                                    : message.sender_type === 'system'
                                    ? 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300 rounded-lg border border-yellow-200 dark:border-yellow-800'
                                    : 'bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-white rounded-bl-none'
                                }`}>
                                  <p className="text-sm break-words leading-relaxed">
                                    {message.content}
                                  </p>
                                  
                                  <div className={`text-[10px] mt-2 flex items-center gap-1 ${
                                    alignment.align === 'right'
                                      ? 'justify-end text-blue-100'
                                      : alignment.isAdmin || alignment.isSupport
                                      ? 'justify-start text-white/70'
                                      : message.sender_type === 'system'
                                      ? 'justify-start text-yellow-600 dark:text-yellow-400'
                                      : 'justify-start text-gray-500 dark:text-gray-400'
                                  }`}>
                                    <span>{formatTime(message.created_at)}</span>
                                    {alignment.align === 'right' && message.read_at && (
                                      <span className="text-[8px]">✓✓</span>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                      <div ref={messagesEndRef} />
                    </>
                  )}
                </div>
              </div>
            )}
          </div>

          {step === 'chat' && (
            <div className="border-t border-gray-200 dark:border-gray-700 p-4 bg-white dark:bg-gray-800">
              {error && !showEndConfirm && (
                <div className="flex items-start gap-2 p-3 mb-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                  <AlertCircle className="w-4 h-4 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
                </div>
              )}

              {showEndConfirm && (
                <div className="mb-4 p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
                  <p className="text-sm font-semibold text-yellow-800 dark:text-yellow-200 mb-2">
                    End this chat?
                  </p>
                  <p className="text-xs text-yellow-700 dark:text-yellow-300 mb-3">
                    The chat will be marked as ended. You can still view it in your chat history.
                  </p>
                  <div className="flex gap-2">
                    <button
                      onClick={handleEndChat}
                      disabled={isLoading}
                      className="flex-1 px-3 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-semibold transition disabled:opacity-50"
                    >
                      {isLoading ? 'Ending...' : 'Yes, End Chat'}
                    </button>
                    <button
                      onClick={() => setShowEndConfirm(false)}
                      className="flex-1 px-3 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg text-sm font-semibold transition"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}

              <form onSubmit={handleSendMessage} className="flex gap-2">
                <input
                  type="text"
                  value={inputMessage}
                  onChange={(e) => setInputMessage(e.target.value)}
                  placeholder="Type your message..."
                  disabled={isLoading}
                  className="flex-1 px-4 py-3 border-2 border-gray-300 dark:border-gray-600 rounded-full focus:border-blue-500 dark:bg-gray-700 dark:text-white outline-none transition disabled:opacity-50"
                />
                <button
                  type="submit"
                  disabled={isLoading || !inputMessage.trim()}
                  className="px-5 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-full font-semibold hover:shadow-lg transition disabled:opacity-50 flex items-center justify-center"
                >
                  {isLoading ? (
                    <Loader className="w-4 h-4 animate-spin" />
                  ) : (
                    <Send className="w-4 h-4" />
                  )}
                </button>
              </form>

              <div className="flex justify-between items-center mt-3">
                <button
                  onClick={() => setStep('initial')}
                  className="text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 transition flex items-center gap-1"
                >
                  ← Back to chats
                </button>
                
                <button
                  onClick={() => setShowEndConfirm(true)}
                  className="text-xs text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 transition flex items-center gap-1 font-semibold"
                >
                  <Trash2 className="w-3 h-3" />
                  End Chat
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      <button
        onClick={() => setIsOpen(!isOpen)}
        className="fixed bottom-6 right-6 z-40 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white p-4 rounded-full shadow-2xl transition-all duration-300 hover:scale-110 group md:animate-bounce-slow"
        aria-label="Open live chat"
      >
        <div className="absolute inset-0 bg-blue-600 rounded-full opacity-50 blur-xl animate-pulse"></div>
        
        <div className="relative">
          {isOpen ? (
            <X className="w-6 h-6" />
          ) : (
            <>
              <MessageCircle className="w-6 h-6" />
              <span className={`absolute -top-2 -right-2 w-4 h-4 rounded-full animate-pulse ${adminOnline ? 'bg-green-400' : 'bg-yellow-400'}`}></span>
            </>
          )}
        </div>

        <span className="hidden md:block absolute right-full mr-4 top-1/2 -translate-y-1/2 bg-gray-900 dark:bg-gray-800 text-white px-4 py-2 rounded-lg text-sm font-semibold whitespace-nowrap opacity-0 pointer-events-none group-hover:opacity-100 transition-opacity duration-200 shadow-xl">
          💬 Live Chat Support
        </span>
      </button>

      {isOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 md:hidden"
          onClick={() => setIsOpen(false)}
        />
      )}

      <style jsx>{`
        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes bounce-slow {
          0%, 100% {
            transform: translateY(0);
          }
          50% {
            transform: translateY(-10px);
          }
        }

        .animate-slideUp {
          animation: slideUp 0.3s ease-out;
        }

        @media (min-width: 768px) {
          .animate-bounce-slow {
            animation: bounce-slow 2s ease-in-out infinite;
          }
        }
      `}</style>
    </>
  );
}